<script lang="ts">
export default {
  name: 'AccountProgramDetails',
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, onUpdated, inject } from 'vue';
import { useRoute } from 'vue-router';
import PolicyDetails from '@/views/policy/PolicyDetails.vue';
import { EpicPolicy } from '@/types/tower/Domain';
import { accountService } from '@/services/api';
import { Pencil, Trash2, ChevronLeft } from 'lucide-vue-next';
import { Program } from '@/types/Programs';
import FilterBar from '@/components/FilterBar.vue';
import { IsUserAClient } from '@/utilities/AuthUtils';
import { AppState } from '@/store';
import { groupedProgramRates, ProgramRateGroup } from '@/types/ProgramRates';
import Renewals from '@/components/Renewals.vue';
import AccountProgramDetailTabs, { DetailTab } from '@/components/AccountProgramDetailTabs.vue';
import { getElementHeight } from '@/utilities/DOM';
import { Store } from 'vuex';
import AccountTowerDashboard from './tower/AccountTowerDashboard.vue';
import AccountTowerVisualizer from '@/views/account/tower/AccountTowerVisualizer.vue';
import { towerService as towerServiceV2 } from '@/services/v2/TowerService';
import Skeleton from '@/components/v2/Skeleton.vue';

const $route = useRoute();

const POLICIES_TOGGLE = 'POLICIES_TOGGLE';
const TOWER_V2_TOGGLE = 'TOWER_V2_TOGGLE';
const RATE_CHANGE_TOGGLE = 'RATE_CHANGE_TOGGLE';

// -----
// data
// -----
const loading = ref(false);
const program = ref<Program | undefined>(undefined);
const policies = ref<EpicPolicy[]>([]);
const programRateGroups = ref<ProgramRateGroup[]>([]);
const tabs = ref<DetailTab[]>([]);
const selectedTab = ref('POLICIES_TOGGLE');
const towerBetaSubHeaderHeight = ref(0);
const showTowerDashboard = ref(true);
const showTowerVisualizer = ref(false);
const serializedTower = ref<string>('');
const hasPublishedTowers = ref(false);

const store = inject<Store<AppState>>('store');

// ---------
// computed
// ---------
const programId = computed(() => {
  return $route.params.programId as string;
});

const isAutomatedProgram = computed(() => {
  return !!program.value && program.value.type === 'Automated';
});

const isAClientAccount = computed(() => {
  return IsUserAClient(store?.state.user);
});

const accountId = computed(() => {
  return $route.params.id as string;
});

// ---------
// watchers
// ---------

// -----------
// lifecycles
// -----------
onMounted(async () => {
  await refreshPolicies();

  hasPublishedTowers.value =
    (await towerServiceV2.getPublishedTowerCountByAccount(accountId.value, program.value?.id)) > 0;

  tabs.value = [{ title: 'Policies', value: POLICIES_TOGGLE }];

  if (!isAClientAccount.value || (isAClientAccount.value && hasPublishedTowers.value)) {
    tabs.value.push({ title: 'Tower', value: TOWER_V2_TOGGLE });
  }

  if (programRateGroups.value.length > 0) {
    tabs.value.push({ title: 'Rate Change', value: RATE_CHANGE_TOGGLE });
  }
});

onUpdated(async () => {
  towerBetaSubHeaderHeight.value = getElementHeight(document.getElementById('tower-beta-sub-header'));
});

// ----------
// functions
// ----------
function changeSelectedTab(tab: string): void {
  selectedTab.value = tab;
}

async function refreshPolicies(): Promise<void> {
  loading.value = true;

  const [programDetailsResult, programRatesResult] = await Promise.all([
    accountService.getProgramDetails(accountId.value, programId.value, isAClientAccount.value),
    accountService.getProgramRates(accountId.value),
  ]);

  program.value = programDetailsResult.program;
  policies.value = programDetailsResult.policies;

  const programCoverageTypes = new Set<string>(program.value.coverageTypes);
  const blah = programRatesResult.filter((r) => r.coverageType && programCoverageTypes.has(r.coverageType));

  programRateGroups.value = groupedProgramRates(blah);

  loading.value = false;
}

async function removePolicyFromProgram(policy: EpicPolicy): Promise<void> {
  if (policy.uniqPolicy) {
    await accountService.removePolicyFromProgram(
      $route.params.id as string,
      $route.params.programId as string,
      policy.uniqPolicy,
    );
    await refreshPolicies();
  }
}

function getRemoveFromProgramMenuItems(policy: EpicPolicy) {
  return [
    {
      label: 'Remove from Program',
      icon: Trash2,
      handler: () => removePolicyFromProgram(policy),
      testId: 'remove-program-policy',
      iconColorClass: 'tw:text-error',
    },
  ];
}

function handleViewTowers(tower: string): void {
  showTowerDashboard.value = false;
  showTowerVisualizer.value = true;
  serializedTower.value = tower;
}
</script>

<template>
  <div class="tw:flex tw:h-full tw:flex-col">
    <div v-if="loading" class="tw:space-y-2 tw:px-8 tw:py-4">
      <Skeleton v-for="i in 4" :key="i" shape="rectangle" width="100%" height="48px" />
    </div>

    <!-- Policies -->

    <PolicyDetails
      v-else-if="selectedTab === POLICIES_TOGGLE"
      scenario="program"
      :hide-more-filters="true"
      :preloadedPolicies="policies"
      :title="`${program ? program.name : ''} Details`"
      :showFilterToggles="false"
      :get-extra-menu-items="!isAutomatedProgram && !isAClientAccount ? getRemoveFromProgramMenuItems : undefined">
      <template v-slot:filter-bar-left>
        <div class="tw:flex tw:flex-col tw:justify-center tw:py-2 tw:pr-2">
          <router-link :to="`/account/${$route.params.id}/programs`" color="primary" class="tw:no-underline">
            <div class="tw:flex tw:items-center">
              <ChevronLeft :size="16" />
              Back To Programs
            </div>
          </router-link>
        </div>
      </template>
      <template v-slot:inner>
        <AccountProgramDetailTabs
          :tabs="tabs"
          :selected-tab="POLICIES_TOGGLE"
          @change="(tab) => changeSelectedTab(tab)" />
      </template>
      <template v-slot:inner-append>
        <router-link
          v-if="!isAutomatedProgram && !isAClientAccount"
          :to="`/account/${$route.params.id}/programs/${program?.id}/edit`"
          title="Edit Program"
          data-test="edit-btn"
          class="tw:inline-flex tw:items-center tw:justify-center tw:rounded tw:p-2 tw:text-primary hover:tw:bg-gray-100">
          <Pencil :size="16" />
        </router-link>
        <div class="tw:grow"></div>
      </template>
    </PolicyDetails>

    <!-- Tower -->

    <template v-if="selectedTab === TOWER_V2_TOGGLE">
      <div id="tower-beta-sub-header" class="">
        <FilterBar class="filter-bar">
          <div class="tw:flex tw:flex-col tw:justify-center tw:py-2 tw:pr-2">
            <router-link :to="`/account/${$route.params.id}/programs`" color="primary" class="tw:no-underline">
              <div class="tw:flex tw:items-center">
                <ChevronLeft :size="16" />
                Back To Programs
              </div>
            </router-link>
          </div>
        </FilterBar>
        <div
          class="tw:border-t-0 tw:border-r-0 tw:border-b tw:border-l-0 tw:border-solid tw:border-gray-300 tw:px-8 tw:py-4">
          <AccountProgramDetailTabs
            :tabs="tabs"
            :selected-tab="TOWER_V2_TOGGLE"
            @change="(tab) => changeSelectedTab(tab)" />
        </div>
      </div>
      <div class="tw:relative tw:h-full tw:w-full">
        <AccountTowerDashboard
          v-if="showTowerDashboard"
          :accountId="accountId"
          :programId="programId"
          @view-towers="handleViewTowers" />
        <AccountTowerVisualizer
          v-if="showTowerVisualizer"
          :serialized-tower="serializedTower"
          @go-back="
            showTowerDashboard = true;
            showTowerVisualizer = false;
          " />
      </div>
    </template>

    <!-- Renewals -->

    <template v-if="selectedTab === RATE_CHANGE_TOGGLE">
      <FilterBar class="filter-bar">
        <div class="tw:flex tw:flex-col tw:justify-center tw:py-2 tw:pr-2">
          <router-link :to="`/account/${$route.params.id}/programs`" color="primary" class="tw:no-underline">
            <div class="tw:flex tw:items-center">
              <ChevronLeft :size="16" />
              Back To Programs
            </div>
          </router-link>
        </div>
      </FilterBar>

      <div class="tw:px-8 tw:py-4">
        <AccountProgramDetailTabs
          :tabs="tabs"
          :selected-tab="RATE_CHANGE_TOGGLE"
          @change="
            (tab) => {
              changeSelectedTab(tab);
            }
          " />

        <h2 class="mb-4">Rate Change</h2>
        <Renewals :account-id="accountId" :program-rate-groups="programRateGroups" />
      </div>
    </template>
  </div>
</template>
